// ---------------------------------------------------------------------------
// TransitionButtons — Role-aware action buttons for status transitions
// ---------------------------------------------------------------------------

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { useTransitionTemplate } from '@/hooks/use-workflow';
import { getNextStatuses, transitionLabel, isCommentRequired } from '@/types/workflow';
import type { CmsRole } from '@/types/workflow';
import type { TemplateStatus } from '@/types/template';

interface TransitionButtonsProps {
  templateId: string;
  currentStatus: TemplateStatus;
  role: CmsRole;
}

export function TransitionButtons({ templateId, currentStatus, role }: TransitionButtonsProps) {
  const { toast } = useToast();
  const transition = useTransitionTemplate(templateId);
  const nextStatuses = getNextStatuses(role, currentStatus);

  const [confirmStatus, setConfirmStatus] = useState<TemplateStatus | null>(null);
  const [comment, setComment] = useState('');

  async function handleConfirm() {
    if (!confirmStatus) return;
    // CMS-03a: comment required on request-revisions transitions (UI guard;
    // backend approval-workflow.ts also enforces).
    if (isCommentRequired(confirmStatus) && !comment.trim()) {
      toast({
        title: 'Comment required',
        description: 'Please explain what needs to be revised.',
        variant: 'destructive',
      });
      return;
    }
    try {
      await transition.mutateAsync({ toStatus: confirmStatus, comment: comment || undefined });
      toast({
        title: `Status changed to ${transitionLabel(confirmStatus)}`,
        variant: 'success',
      });
      setConfirmStatus(null);
      setComment('');
    } catch {
      toast({ title: 'Transition failed', variant: 'destructive' });
    }
  }

  if (nextStatuses.length === 0) return null;

  return (
    <>
      {nextStatuses.map((status) => {
        const isDestructive = status === 'revision_requested' || status === 'draft';
        const label =
          status === 'draft' && currentStatus === 'pending_internal_review'
            ? 'Request Revision'
            : transitionLabel(status);
        return (
          <Button
            key={status}
            variant={isDestructive ? 'destructive' : 'default'}
            size="sm"
            onClick={() => setConfirmStatus(status)}
          >
            {label}
          </Button>
        );
      })}

      {/* Confirmation dialog */}
      {confirmStatus && (
        <Dialog open={!!confirmStatus} onOpenChange={(open) => { if (!open) setConfirmStatus(null); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Confirm Transition</DialogTitle>
              <DialogDescription>
                Change status to <strong>{
                  confirmStatus === 'draft' && currentStatus === 'pending_internal_review'
                    ? 'Request Revision'
                    : transitionLabel(confirmStatus)
                }</strong>?
              </DialogDescription>
            </DialogHeader>
            <div className="mt-2">
              <Label className="mb-1">
                Comment {isCommentRequired(confirmStatus) ? '(required)' : '(optional)'}
              </Label>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={
                  isCommentRequired(confirmStatus)
                    ? 'Describe what needs to be revised...'
                    : 'Add a note about this transition...'
                }
                className="min-h-[60px]"
                required={isCommentRequired(confirmStatus)}
              />
            </div>
            <DialogFooter className="mt-4">
              <Button variant="secondary" onClick={() => setConfirmStatus(null)}>
                Cancel
              </Button>
              <Button onClick={handleConfirm} disabled={transition.isPending}>
                {transition.isPending ? 'Processing...' : 'Confirm'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
